/**
 * Google Sheets sync — sheets-sync.ts
 *
 * Fetches service data from Spencer's published Google Sheet CSV and maps it
 * into ServiceEntry objects. The sheet is the single source of truth for
 * pricing, services, and upsell relationships.
 *
 * ── How sync works ─────────────────────────────────────────────────────────────
 * • On every page load the site checks the cache age.
 * • If cache is > 1 hour old (or missing), it fetches the sheet in the background.
 * • Components always render instantly from cache — no loading spinners.
 * • Hitting "Sync Now" in /admin/services forces an immediate refresh.
 *
 * ── Sheet column reference ─────────────────────────────────────────────────────
 *
 *  service_id           Unique slug, e.g. ps5_hdmi. Never change once set.
 *  active               TRUE = shown to customers | FALSE = hidden
 *  category             PS5 | Xbox | Nintendo | Controller | Laptop | Handheld PC
 *  subcategory          Internal grouping (HDMI, Overheating, Stick Drift, …)
 *  service_name         Display name shown to customers
 *  short_description    One-line description
 *  price                Flat price number (e.g. 160) — blank = "Get a Quote"
 *  duration_minutes     Internal estimate (not shown publicly)
 *  location_availability Both | San Diego | Temecula
 *  what_to_bring        e.g. "Console only. No cables needed."
 *  customer_notes_prompt Placeholder text for the customer notes field
 *  upsell_ids           Comma-separated service_ids to suggest as add-ons,
 *                       e.g.  ps5_cleaning,mag_breakaway
 *  image_url            Optional image URL
 *  turnaround_time      Human-readable, e.g. "Same day–1 business day"
 *  priority             Sort order (lower = shown first) — not currently used
 *  repairshopr_subject  RepairShopr ticket subject template (internal)
 *  confirmation_notes   Extra note shown on the booking confirmation screen
 *  seo_slug             URL slug for SEO (internal)
 *
 * ── Adding new services ────────────────────────────────────────────────────────
 * 1. Add a new row to the sheet.
 * 2. Fill in service_id (unique), category, service_name, price, active = TRUE.
 * 3. To make it an upsell: add its service_id to the upsell_ids column of the
 *    service(s) it should be suggested with.
 * 4. Save the sheet — the site picks it up on the next sync (≤ 1 hour).
 *
 * ── Adding new upsell relationships ───────────────────────────────────────────
 * Find the main service row, add the upsell's service_id to its upsell_ids cell
 * (comma-separated). The upsell service itself just needs active = TRUE —
 * no special flag required; the parser auto-detects it from the references.
 */

import type { ServiceEntry, DeviceCategory } from '../types/services';

// ── Cache ─────────────────────────────────────────────────────────────────────

export const SHEETS_CACHE_KEY   = 'gfs_sheets_cache_v5'; // v5: requiresParentService, eligibleParentServiceIds, serviceType
export const SHEETS_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

export interface SkippedRow {
  row:    number;   // 1-based data row number (row 2 in sheet = row 1 here)
  id:     string;
  name:   string;
  reason: 'inactive' | 'missing_id' | 'missing_name' | 'unknown_category';
  raw:    string;   // raw category value for unknown_category skips
}

export interface SheetsCache {
  fetchedAt: number;
  services:  ServiceEntry[];
  rowCount:  number;   // total data rows in sheet (including inactive)
  sheetUrl:  string;
  skipped?:  SkippedRow[];
}

export interface SyncResult {
  ok:       boolean;
  services: ServiceEntry[];
  rowCount: number;
  skipped:  SkippedRow[];
  error?:   string;
}

export function getCachedSheetData(): SheetsCache | null {
  try {
    const raw = localStorage.getItem(SHEETS_CACHE_KEY);
    return raw ? (JSON.parse(raw) as SheetsCache) : null;
  } catch {
    return null;
  }
}

export function isCacheFresh(fetchedAt: number): boolean {
  return Date.now() - fetchedAt < SHEETS_CACHE_TTL_MS;
}

/** Human-readable "X minutes/hours ago" for the admin sync bar */
export function syncAgeLabel(fetchedAt: number): string {
  const ms   = Date.now() - fetchedAt;
  const mins = Math.floor(ms / 60_000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins} min ago`;
  return `${Math.floor(mins / 60)} hr ago`;
}

// ── CSV parser ────────────────────────────────────────────────────────────────
// Handles quoted fields, embedded commas, and escaped double-quotes ("").

function parseCSV(text: string): string[][] {
  const rows: string[][] = [];
  let cell = '', cells: string[] = [], inQuote = false;

  const flush = () => { cells.push(cell.trim()); cell = ''; };
  const commit = () => {
    flush();
    if (cells.some(c => c !== '')) rows.push([...cells]);
    cells = [];
  };

  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (c === '"') {
      if (inQuote && text[i + 1] === '"') { cell += '"'; i++; }
      else inQuote = !inQuote;
    } else if (c === ',' && !inQuote) { flush(); }
    else if ((c === '\n' || c === '\r') && !inQuote) {
      if (c === '\r' && text[i + 1] === '\n') i++;
      commit();
    } else { cell += c; }
  }
  commit();
  return rows;
}

// ── Column index ──────────────────────────────────────────────────────────────

function buildIdx(headers: string[]): Map<string, number> {
  const m = new Map<string, number>();
  headers.forEach((h, i) => m.set(h.trim().toLowerCase(), i));
  return m;
}

function col(idx: Map<string, number>, row: string[], name: string): string {
  const i = idx.get(name);
  return i !== undefined ? (row[i] ?? '').trim() : '';
}

// ── Category mapping ──────────────────────────────────────────────────────────
// Maps your sheet's category names to the internal DeviceCategory values.
// Add new entries here if you add new categories to the sheet.

const CATEGORY_MAP: Record<string, DeviceCategory> = {
  ps5:                 'ps5',
  xbox:                'xbox',
  switch:              'switch',
  nintendo:            'switch',          // "Nintendo" tab covers Switch models
  'switch 2':          'switch',          // Switch 2 rows may use this as category
  controller:          'controller',
  'controller repair': 'controller',   // sheet uses "Controller Repair" as category value
  laptop:              'laptop-macbook',
  macbook:             'laptop-macbook',
  'laptop-macbook':    'laptop-macbook',
  'laptop / pc':       'laptop-macbook',
  'laptop/pc':         'laptop-macbook',
  'handheld pc':       'handheld-pc',
  'handheld-pc':       'handheld-pc',
  handheld:            'handheld-pc',
  'gaming handheld':   'handheld-pc',    // sheet category name
  'handheld gaming pc':'handheld-pc',
  'pc handheld':       'handheld-pc',    // sheet category name for Steam Deck, ROG Ally, Legion Go, MSI Claw
  'nintendo handheld': 'nintendo-handheld',
  retro:               'nintendo-handheld',
};

function mapCategory(raw: string): DeviceCategory | null {
  return CATEGORY_MAP[raw.toLowerCase().trim()] ?? null;
}

// ── Price parser ──────────────────────────────────────────────────────────────

function parsePrice(v: string): number | null {
  if (!v || /^(quote|tbd|call|-)$/i.test(v)) return null;
  const n = parseFloat(v.replace(/[$,\s]/g, ''));
  return isNaN(n) ? null : n;
}

// ── Row → ServiceEntry ────────────────────────────────────────────────────────

function rowToService(
  idx: Map<string, number>,
  row: string[],
  now: string,
): ServiceEntry | null {
  const g = (name: string) => col(idx, row, name);

  const id       = g('service_id');
  const name     = g('service_name');
  const category = mapCategory(g('category'));

  // Skip blank rows, header repeats, or unknown categories
  if (!id || !name || !category) return null;

  // active column: TRUE = active, anything else = inactive
  const isActive = /^(true|yes|1)$/i.test(g('active'));

  // compatible_models: comma- or semicolon-separated model slugs
  // blank = applies to all models in the category
  const deviceModels = g('compatible_models')
    .split(/[,;]/)
    .map(s => s.trim())
    .filter(Boolean);

  // upsell_ids: comma- or semicolon-separated service IDs
  const upsellIds = g('upsell_ids')
    .split(/[,;]/)
    .map(s => s.trim())
    .filter(Boolean);

  // Scheduling rule columns (added to sheet — safe to read; absent = default false/empty)
  const requiresParentService = /^(true|yes|1)$/i.test(g('requires_parent_service'));
  const eligibleParentServiceIds = g('eligible_parent_service_ids')
    .split(/[,;]/)
    .map(s => s.trim())
    .filter(Boolean);
  const serviceType = g('service_type').toLowerCase().replace(/[\s-]/g, '_') || 'repair';

  return {
    id,
    sku:           id,                          // reuse service_id as internal SKU
    repairType:    name,
    deviceCategory: category,
    deviceModels,
    repairCategory: 'repair',
    basePrice:     parsePrice(g('price')),
    rushPrice:     null,
    priceTiers:    [],
    description:   g('short_description'),
    turnaround:    g('turnaround_time'),
    rushTurnaround: '',
    warranty:      '',
    mailInAvailable: true,
    internalNotes: g('repairshopr_subject'),    // stash for future RepairShopr use
    disclaimer:    g('confirmation_notes'),
    tags:          [g('subcategory')].filter(Boolean),
    imageUrl:      g('image_url'),
    imageAlt:      name,
    whatToBring:   g('what_to_bring'),
    upsellIds,
    isUpsell:      false,                       // computed in second pass below
    requiresParentService,
    eligibleParentServiceIds,
    serviceType,
    status:        isActive ? 'active' : 'inactive',
    createdAt:     now,
    updatedAt:     now,
  };
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Fetch, parse, and cache services from the published Google Sheet CSV URL.
 *
 * After parsing, runs a second pass to mark any service whose service_id
 * appears in another row's upsell_ids as isUpsell = true — so you never
 * have to set that flag manually in the sheet.
 */
export async function syncServicesFromSheet(sheetUrl: string): Promise<SyncResult> {
  const now = new Date().toISOString();
  try {
    console.info(`[GameFix Sheets] Fetching: ${sheetUrl}`);
    const res = await fetch(sheetUrl, { cache: 'no-store' });
    console.info(`[GameFix Sheets] HTTP ${res.status} ${res.statusText}`);
    if (!res.ok) throw new Error(`HTTP ${res.status} — check the sheet is published to the web`);

    // Strip UTF-8 BOM if present — Google Sheets CSV exports sometimes include
    // ﻿ at the start, which silently prefixes the first column header.
    const raw = await res.text();
    const text = raw.charCodeAt(0) === 0xFEFF ? raw.slice(1) : raw;

    // ── Pre-parse diagnostic ──────────────────────────────────────────────────
    const rawLines = text.split(/\r?\n/);
    console.info(`[GameFix Sheets] Total lines received: ${rawLines.length}`);
    console.info(`[GameFix Sheets] First 5 lines:`);
    rawLines.slice(0, 5).forEach((line, i) => console.info(`  [${i}] ${line}`));
    console.info(`[GameFix Sheets] Row 0 (exact): ${JSON.stringify(rawLines[0])}`);
    // ─────────────────────────────────────────────────────────────────────────

    const rows = parseCSV(text);
    if (rows.length < 2) throw new Error('Sheet appears empty — is row 1 the header row?');

    const [headerRow, ...dataRows] = rows;
    const idx = buildIdx(headerRow);

    console.info(`[GameFix Sheets] Parsed headers: ${JSON.stringify(headerRow)}`);

    if (!idx.has('service_id')) throw new Error('Missing "service_id" column — check the sheet header row');
    if (!idx.has('service_name')) throw new Error('Missing "service_name" column — check the sheet header row');

    // First pass: parse all rows, collecting skip reasons
    const skipped: SkippedRow[] = [];
    const services: ServiceEntry[] = [];

    dataRows.forEach((row, i) => {
      const id       = col(idx, row, 'service_id');
      const name     = col(idx, row, 'service_name');
      const rawCat   = col(idx, row, 'category');
      const isActive = /^(true|yes|1)$/i.test(col(idx, row, 'active'));

      const rowNum = i + 1;

      if (!isActive) {
        skipped.push({ row: rowNum, id, name, reason: 'inactive', raw: rawCat });
        return;
      }
      if (!id) {
        skipped.push({ row: rowNum, id, name, reason: 'missing_id', raw: rawCat });
        return;
      }
      if (!name) {
        skipped.push({ row: rowNum, id, name, reason: 'missing_name', raw: rawCat });
        return;
      }
      if (!mapCategory(rawCat)) {
        skipped.push({ row: rowNum, id, name, reason: 'unknown_category', raw: rawCat });
        console.warn(`[GameFix Sheets] ⚠️ Row ${rowNum + 1}: skipping "${name}" — unknown category "${rawCat}"`);
        return;
      }

      const svc = rowToService(idx, row, now);
      if (svc) services.push(svc);
    });

    if (services.length === 0) {
      throw new Error('Parsed 0 services — check that "category" values match the allowed list (PS5, Xbox, Nintendo, Controller, Controller Repair, etc.)');
    }

    // Second pass: mark add-ons.
    // A service is an add-on if: its ID appears in another service's upsellIds,
    // OR requires_parent_service = TRUE, OR service_type = 'add_on'.
    const allUpsellIds = new Set(services.flatMap(s => s.upsellIds));
    for (const s of services) {
      if (allUpsellIds.has(s.id) || s.requiresParentService || s.serviceType === 'add_on') {
        s.isUpsell = true;
      }
    }

    const unknownCatSkips = skipped.filter(s => s.reason === 'unknown_category');
    if (unknownCatSkips.length > 0) {
      console.warn(`[GameFix Sheets] ⚠️ ${unknownCatSkips.length} rows skipped — unknown category values: ${[...new Set(unknownCatSkips.map(s => s.raw))].join(', ')}`);
    }

    // Persist to cache (guarded — Safari Private Browsing throws on localStorage write)
    const cache: SheetsCache = { fetchedAt: Date.now(), services, rowCount: dataRows.length, sheetUrl, skipped };
    try { localStorage.setItem(SHEETS_CACHE_KEY, JSON.stringify(cache)); } catch { /* unavailable */ }

    console.info(`[GameFix Sheets] ✅ Synced ${services.filter(s => s.status === 'active').length} active / ${services.length} total from ${dataRows.length} rows (${skipped.filter(s => s.reason === 'unknown_category').length} unknown-cat skips)`);
    return { ok: true, services, rowCount: dataRows.length, skipped };

  } catch (err) {
    const error = err instanceof Error ? err.message : String(err);
    console.warn(`[GameFix Sheets] ⚠️ Sync failed: ${error}`);
    return { ok: false, services: [], rowCount: 0, skipped: [], error };
  }
}
